import { BoxV2 as Box, Button, Flex, RoleGuard } from 'portal-commons';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import CarrierStatusTable from './CarrierStatusTable';
import AcceptCampaignModal from '../AcceptCampaignModal';
import CampaignsActionDialog from '../CampaignsActionDialog';
import {
  CspCampaignDeclineModal,
  DcaCampaignDeclineModal,
} from '../CampaignActionModal';
import CampaignsByPrimaryDcaActionDialog from '../CampaignsByPrimaryDcaActionDialog';
import ElectCNPAlert from '../ElectCNPAlert';
import MnoExclusions from '../MnoExclusions';
import RemoveExclusionModal from '../RemoveExclusionModal';
import { rejectPrimaryDcaCampaign } from '../../apis';
import { CampaignDetail, CampaignOperationItem } from '../../types';
import { campaignController } from '@/apis/core';
import { ExcludedMno } from '@/apis/core/types';
import { getApprovedMnoOptions } from '@/apis/services';
import { getRejectionCategoryOptions } from '@/apis/services/enums';
import { CAMPAIGN_REJECTION_ENABLED } from '@/constants';
import {
  CSP_CAMPAIGNS_DEFAULT_ENTRY,
  DCA_CAMPAIGNS_DEFAULT_ENTRY,
} from '@/constants/paths';
import { UserProfile } from '@/features/users/types';
import { SelectItem } from '@/types';
import { sortMnoList } from '@/utils';

interface Props {
  profile: UserProfile;
  campaign: CampaignDetail;
  provisional?: boolean;
  existingChain: boolean;
  goBackPage?: string;
  onAcceptElection: () => Promise<void>;
  onMnoExclusionRemove?: (mno: ExcludedMno) => void;
}

enum ModalType {
  None = 'none',
  AcceptCampaign = 'acceptCampaign',
  RemoveExclusion = 'removeExclusion',
}

const CarrierStatusSection: React.FC<Props> = ({
  profile,
  campaign,
  provisional = false,
  existingChain,
  goBackPage,
  onAcceptElection,
  onMnoExclusionRemove,
}) => {
  const [loading, setLoading] = useState(true);
  const [openElectAlert, setOpenElectAlert] = useState(false);
  const [showNominationPopup, setShowNominationPopup] = useState(false);
  const [actionLoader, setActionLoader] = useState(false);
  const [reject, setReject] = useState(false);
  const [campaignOperations, setCampaignOperations] = useState<
    CampaignOperationItem[]
  >([]);
  const [rejectionCategories, setRejectionCategories] = useState<SelectItem[]>(
    []
  );
  const [approvedMnoOptions, setApprovedMnoOptions] = useState<SelectItem[]>(
    []
  );
  const [modalType, setModalType] = useState<ModalType>(ModalType.None);
  const [activeMno, setActiveMno] = useState<ExcludedMno>();
  const history = useHistory();
  const { isPrimaryDca, sharingStatus, secondarySharingStatus } =
    campaign || {};

  const updateApprovedMnoOptions = async () => {
    const options = await getApprovedMnoOptions();
    setApprovedMnoOptions(options);
  };

  const fetchRejectionCategories = async () => {
    const options = await getRejectionCategoryOptions();
    setRejectionCategories(options);
  };

  useEffect(() => {
    updateApprovedMnoOptions();
    CAMPAIGN_REJECTION_ENABLED && fetchRejectionCategories();
  }, []);

  useEffect(() => {
    if (provisional !== null && provisional !== undefined) {
      const getCampaignOperations = async () => {
        await fetchCampaignOperations(campaign.uid);
      };
      getCampaignOperations();
    }

    const showNomination = !!isPrimaryDca
      ? sharingStatus === 'PENDING'
      : secondarySharingStatus === 'PENDING';
    setShowNominationPopup(showNomination);
  }, [campaign, provisional]);

  const fetchCampaignOperations = async (campaignUid) => {
    setLoading(true);
    const operations = await campaignController.getCampaignOperations(
      campaignUid,
      provisional
    );
    if (operations) {
      const carriers = operations.map((data) => ({
        ...data,
        rulesEngineResultsJson: data.rulesEngineResultsJson
          ? JSON.parse(data.rulesEngineResultsJson)
          : data.rulesEngineResultsJson,
      }));
      const sortedCarriers = sortMnoList(carriers, [
        'rulesEngineResultsJson',
        'mno',
      ]).filter(
        (carrier) =>
          carrier.rulesEngineResultsJson &&
          carrier.rulesEngineResultsJson.qualify &&
          carrier.rulesEngineResultsJson.mnoSupport
      );
      setCampaignOperations(sortedCarriers);
    }
    setLoading(false);
  };

  const handleUpdateCampaignOperations = () => {
    fetchCampaignOperations(campaign.uid);
  };

  const handleCnpAction = async (action: 'accept' | 'reject') => {
    if (action === 'reject') {
      setReject(true);
      return;
    }

    setActionLoader(true);
    try {
      const acceptApiCall = isPrimaryDca
        ? campaignController.acceptCampaignAsPrimaryDca
        : campaignController.acceptCampaignAsSecondaryDca;

      const result = await acceptApiCall(campaign.uid);
      if (result) {
        setLoading(true);
        await onAcceptElection();
      }
    } finally {
      setActionLoader(false);
    }
  };

  const onDcaMenuClick = () => {
    if (campaign && campaign.usecase === 'SOLE_PROPRIETOR') {
      setOpenElectAlert(true);
    }
  };

  const handleClose = (success = false) => {
    setReject(false);
    if (success) {
      history.push(
        goBackPage ||
          (isPrimaryDca
            ? CSP_CAMPAIGNS_DEFAULT_ENTRY
            : DCA_CAMPAIGNS_DEFAULT_ENTRY)
      );
    }
  };

  const handleSubmit = async (explanation) => {
    setActionLoader(true);
    try {
      await rejectPrimaryDcaCampaign(campaign.uid, explanation);
      setActionLoader(false);
      history.push(goBackPage || DCA_CAMPAIGNS_DEFAULT_ENTRY);
    } catch (e) {}
  };

  return (
    <>
      <Box
        sx={{
          p: 'xxl',
          pt: 'xl',
          mb: 30,
          border: '1px solid',
          borderColor: 't.black20',
          backgroundColor: showNominationPopup
            ? 'rgba(14, 31, 43, 0.65)'
            : 't.white',
        }}
        data-testid="campaignCarrierStatus"
      >
        <Flex
          as="h3"
          sx={{
            alignItems: 'center',
            gap: 'xs',
            fontSize: 'H500',
            fontWeight: 600,
            color: 't.black70',
          }}
        >
          <FontAwesomeIcon icon={faShareNodes} size="lg" />
          Carrier Status
        </Flex>
        {loading ? (
          <Flex
            sx={{
              width: '100%',
              height: '220px',
              alignItems: 'center',
              justifyContent: 'center',
              color: 't.black20',
              fontSize: 'H400',
            }}
          >
            loading...
          </Flex>
        ) : showNominationPopup ? (
          <Flex
            sx={{
              width: 525,
              height: 160,
              m: 'auto',
              backgroundColor: 't.white',
              borderRadius: 4,
              padding: '24px 32px 8px',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
            data-testid="cnpNominationPopup"
          >
            <Box
              as="p"
              sx={{ pt: 7, pb: 11, fontSize: 'H300', color: 't.black80' }}
            >
              You have been elected as a Connectivity Partner for this campaign.
            </Box>
            <Flex sx={{ alignItems: 'center', gap: 18, pt: 'xl', pb: 'xxl' }}>
              <RoleGuard
                feature="campaignDetail.declineConnectivityPartner"
                render={(hasPermission) => {
                  return (
                    <Button
                      variant="outline"
                      disabled={actionLoader || !hasPermission}
                      data-testid="cnpNominationPopupDeclineButton"
                      onClick={() => handleCnpAction('reject')}
                    >
                      Decline
                    </Button>
                  );
                }}
              />
              <RoleGuard
                feature="campaignDetail.acceptConnectivityPartner"
                render={(hasPermission) => {
                  return (
                    <Button
                      disabled={actionLoader || !hasPermission}
                      onClick={() => setModalType(ModalType.AcceptCampaign)}
                      data-testid="cnpNominationPopupAcceptButton"
                    >
                      Accept
                    </Button>
                  );
                }}
              />
            </Flex>
          </Flex>
        ) : (
          <Flex sx={{ flexDirection: 'column', gap: 'l' }}>
            <CarrierStatusTable
              profile={profile}
              campaign={campaign}
              carriers={campaignOperations}
              onUpdatedCampaignOperations={handleUpdateCampaignOperations}
              existingChain={existingChain}
              provisional={provisional}
              onDcaMenuClick={onDcaMenuClick}
            />
            {campaign.excludedMnoList &&
              campaign.excludedMnoList.length > 0 && (
                <MnoExclusions
                  mnos={campaign.excludedMnoList}
                  onRemove={(mno) => {
                    setActiveMno(mno);
                    setModalType(ModalType.RemoveExclusion);
                  }}
                />
              )}
          </Flex>
        )}
      </Box>
      <ElectCNPAlert
        open={openElectAlert}
        onClose={() => setOpenElectAlert(false)}
      />
      {CAMPAIGN_REJECTION_ENABLED ? (
        campaign.isPrimaryDca ? (
          <CspCampaignDeclineModal
            open={reject}
            campaign={campaign}
            categories={rejectionCategories}
            onClose={handleClose}
          />
        ) : (
          <DcaCampaignDeclineModal
            open={reject}
            campaignUid={campaign.uid}
            categories={rejectionCategories}
            onClose={handleClose}
          />
        )
      ) : campaign.isPrimaryDca ? (
        <CampaignsActionDialog
          open={reject}
          handleClose={handleClose}
          type={'reject'}
          campaignUid={campaign.uid}
          brandName={campaign.brandName}
          cspUid={campaign.cspUid}
        />
      ) : (
        <CampaignsByPrimaryDcaActionDialog
          type="reject"
          open={reject}
          disabled={actionLoader}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      )}
      <AcceptCampaignModal
        open={modalType === ModalType.AcceptCampaign}
        primaryDca={isPrimaryDca}
        campaignUid={campaign.uid}
        options={approvedMnoOptions}
        onCancel={() => setModalType(ModalType.None)}
        onSubmit={() => {
          onAcceptElection();
          setModalType(ModalType.None);
        }}
      />
      {activeMno && (
        <RemoveExclusionModal
          open={modalType === ModalType.RemoveExclusion}
          campaignUid={campaign.uid}
          mno={activeMno}
          onCancel={() => setModalType(ModalType.None)}
          onSubmit={() => {
            handleUpdateCampaignOperations();
            onMnoExclusionRemove?.(activeMno);
            setModalType(ModalType.None);
            setActiveMno(undefined);
          }}
        />
      )}
    </>
  );
};

export default CarrierStatusSection;
